import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import Footer from './components/Footer';

// Pages
import Home from './pages/Home';
import Udredning from './pages/Udredning';
import ADHD from './pages/ADHD';
import Autisme from './pages/Autisme';
import OmOs from './pages/OmOs';
import Priser from './pages/Priser';
import Kontakt from './pages/Kontakt';
import PsykiatriskeLidelser from './pages/PsykiatriskeLidelser';
import Angst from './pages/Angst';
import Ocd from './pages/Ocd';
import Depression from './pages/Depression';
import Ptsd from './pages/Ptsd';
import Personlighedsforstyrrelser from './pages/Personlighedsforstyrrelser';
import Vidensbank from './pages/Vidensbank';
import VidensbankDetail from './pages/VidensbankDetail';
import AdhdScreening from './pages/AdhdScreening';
import RaadsRScreening from './pages/RaadsRScreening';
import MistankeAdhd from './pages/MistankeAdhd';
import AfbudBetaling from './pages/AfbudBetaling';
import Klagevejledning from './pages/Klagevejledning';
import AkutHjaelp from './pages/AkutHjaelp';
import Privatlivspolitik from './pages/Privatlivspolitik';
import Cookiepolitik from './pages/Cookiepolitik';
import NotFound from './pages/NotFound';
import Forbidden from './pages/Forbidden';
import MedicinskBehandling from './pages/MedicinskBehandling';

function App() {
  return (
    <LanguageProvider>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen bg-sand-light text-text-dark antialiased">
        {/* Sticky Header */}
        <Header />

        {/* Main Content Area */}
        <main className="flex-grow">
          <Routes>
            {/* Danish routes */}
            <Route path="/" element={<Home />} />
            <Route path="/udredning" element={<Udredning />} />
            <Route path="/adhd" element={<ADHD />} />
            <Route path="/autisme" element={<Autisme />} />
            <Route path="/om-os" element={<OmOs />} />
            <Route path="/psykiatriske-lidelser" element={<PsykiatriskeLidelser />} />
            <Route path="/psykiatriske-lidelser/angst" element={<Angst />} />
            <Route path="/psykiatriske-lidelser/ocd" element={<Ocd />} />
            <Route path="/psykiatriske-lidelser/depression" element={<Depression />} />
            <Route path="/psykiatriske-lidelser/ptsd" element={<Ptsd />} />
            <Route path="/psykiatriske-lidelser/personlighedsforstyrrelser" element={<Personlighedsforstyrrelser />} />
            <Route path="/vidensbank" element={<Vidensbank />} />
            <Route path="/vidensbank/adhd-screening" element={<AdhdScreening />} />
            <Route path="/vidensbank/autisme-screening" element={<RaadsRScreening />} />
            <Route path="/vidensbank/mistanke-om-adhd" element={<MistankeAdhd />} />
            <Route path="/vidensbank/:slug" element={<VidensbankDetail />} />
            <Route path="/priser" element={<Priser />} />
            <Route path="/afbud-og-betaling" element={<AfbudBetaling />} />
            <Route path="/afbud" element={<AfbudBetaling />} />
            <Route path="/klagevejledning" element={<Klagevejledning />} />
            <Route path="/akut-hjaelp" element={<AkutHjaelp />} />
            <Route path="/privatlivspolitik" element={<Privatlivspolitik />} />
            <Route path="/cookiepolitik" element={<Cookiepolitik />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/medicinsk-behandling" element={<MedicinskBehandling />} />
            <Route path="/403" element={<Forbidden />} />

            {/* English routes with English slugs (/en/...) */}
            <Route path="/en" element={<Home />} />
            <Route path="/en/assessment" element={<Udredning />} />
            <Route path="/en/adhd" element={<ADHD />} />
            <Route path="/en/autism" element={<Autisme />} />
            <Route path="/en/about-us" element={<OmOs />} />
            <Route path="/en/psychiatric-conditions" element={<PsykiatriskeLidelser />} />
            <Route path="/en/psychiatric-conditions/anxiety" element={<Angst />} />
            <Route path="/en/psychiatric-conditions/ocd" element={<Ocd />} />
            <Route path="/en/psychiatric-conditions/depression" element={<Depression />} />
            <Route path="/en/psychiatric-conditions/ptsd" element={<Ptsd />} />
            <Route path="/en/psychiatric-conditions/personality-disorders" element={<Personlighedsforstyrrelser />} />
            <Route path="/en/knowledge-base" element={<Vidensbank />} />
            <Route path="/en/knowledge-base/adhd-screening" element={<AdhdScreening />} />
            <Route path="/en/knowledge-base/autism-screening" element={<RaadsRScreening />} />
            <Route path="/en/knowledge-base/suspected-adhd" element={<MistankeAdhd />} />
            <Route path="/en/knowledge-base/:slug" element={<VidensbankDetail />} />
            <Route path="/en/prices" element={<Priser />} />
            <Route path="/en/cancellation-and-payment" element={<AfbudBetaling />} />
            <Route path="/en/cancellation" element={<AfbudBetaling />} />
            <Route path="/en/complaints-procedure" element={<Klagevejledning />} />
            <Route path="/en/emergency-help" element={<AkutHjaelp />} />
            <Route path="/en/privacy-policy" element={<Privatlivspolitik />} />
            <Route path="/en/cookie-policy" element={<Cookiepolitik />} />
            <Route path="/en/contact" element={<Kontakt />} />
            <Route path="/en/medical-treatment" element={<MedicinskBehandling />} />
            <Route path="/en/403" element={<Forbidden />} />

            {/* Backwards-compatibility aliases for legacy /en/da-slug routes */}
            <Route path="/en/udredning" element={<Navigate to="/en/assessment" replace />} />
            <Route path="/en/autisme" element={<Navigate to="/en/autism" replace />} />
            <Route path="/en/om-os" element={<Navigate to="/en/about-us" replace />} />
            <Route path="/en/psykiatriske-lidelser" element={<Navigate to="/en/psychiatric-conditions" replace />} />
            <Route path="/en/psykiatriske-lidelser/angst" element={<Navigate to="/en/psychiatric-conditions/anxiety" replace />} />
            <Route path="/en/psykiatriske-lidelser/ocd" element={<Navigate to="/en/psychiatric-conditions/ocd" replace />} />
            <Route path="/en/psykiatriske-lidelser/depression" element={<Navigate to="/en/psychiatric-conditions/depression" replace />} />
            <Route path="/en/psykiatriske-lidelser/ptsd" element={<Navigate to="/en/psychiatric-conditions/ptsd" replace />} />
            <Route path="/en/psykiatriske-lidelser/personlighedsforstyrrelser" element={<Navigate to="/en/psychiatric-conditions/personality-disorders" replace />} />
            <Route path="/en/vidensbank" element={<Navigate to="/en/knowledge-base" replace />} />
            <Route path="/en/vidensbank/adhd-screening" element={<Navigate to="/en/knowledge-base/adhd-screening" replace />} />
            <Route path="/en/vidensbank/autisme-screening" element={<Navigate to="/en/knowledge-base/autism-screening" replace />} />
            <Route path="/en/vidensbank/mistanke-om-adhd" element={<Navigate to="/en/knowledge-base/suspected-adhd" replace />} />
            <Route path="/en/vidensbank/:slug" element={<VidensbankDetail />} />
            <Route path="/en/priser" element={<Navigate to="/en/prices" replace />} />
            <Route path="/en/afbud-og-betaling" element={<Navigate to="/en/cancellation-and-payment" replace />} />
            <Route path="/en/afbud" element={<Navigate to="/en/cancellation" replace />} />
            <Route path="/en/klagevejledning" element={<Navigate to="/en/complaints-procedure" replace />} />
            <Route path="/en/akut-hjaelp" element={<Navigate to="/en/emergency-help" replace />} />
            <Route path="/en/privatlivspolitik" element={<Navigate to="/en/privacy-policy" replace />} />
            <Route path="/en/cookiepolitik" element={<Navigate to="/en/cookie-policy" replace />} />
            <Route path="/en/kontakt" element={<Navigate to="/en/contact" replace />} />
            <Route path="/en/medicinsk-behandling" element={<Navigate to="/en/medical-treatment" replace />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </LanguageProvider>
  );
}

export default App;
